import React, { useState, useRef, useMemo } from "react";
import Modal from "../base/Modal";
import toast from "react-hot-toast";
import { API_URL } from "../../services/api";
import { DataState } from "../../context/DataProvider";
import AutoGrowTextarea from "../AutoGrowTextarea";

// NEW: Import SchemaBuilder, SchemaViewer, and an icon
import SchemaBuilderModal from "../NewWorkflow/modals/SchemaBuilderModal";
import SchemaViewer from "../NewWorkflow/shared/SchemaViewer";
import { Edit } from 'lucide-react';
import { fieldsToSchema, schemaToFields } from "../NewWorkflow/utils/schemaUtils";

// --- Helper functions to convert between schema and builder's field format ---
// Copied from your StructureNodeModal.js reference for compatibility

// const schemaToFields = (schema) => {
//   if (!schema || !schema.properties) return [];

//   const convertProperties = (properties, requiredFields = []) => {
//     return Object.entries(properties).map(([key, value]) => {
//       const field = {
//         id: `field_${Math.random().toString(36).substring(2, 9)}`,
//         fieldName: key,
//         dataType: value.type.charAt(0).toUpperCase() + value.type.slice(1), // Capitalize e.g., 'string' -> 'String'
//         required: requiredFields.includes(key),
//         description: value.description || '',
//         children: [],
//         arrayItemType: 'String', // Default
//       };

//       if (value.type === 'object' && value.properties) {
//         field.children = convertProperties(value.properties, value.required || []);
//       } else if (value.type === 'array' && value.items) {
//         field.arrayItemType = value.items.type.charAt(0).toUpperCase() + value.items.type.slice(1);
//         if (value.items.type === 'object' && value.items.properties) {
//           field.children = convertProperties(value.items.properties, value.items.required || []);
//         }
//       }
//       return field;
//     });
//   };

//   return convertProperties(schema.properties, schema.required || []);
// };

// const fieldsToSchema = (fields, modelName = 'Output') => {
//     const convert = (fieldList) => {
//         const properties = {};
//         const required = [];

//         fieldList.forEach(field => {
//             if (!field.fieldName) return;
//             let schemaDef = { description: field.description };

//             const dataType = field.dataType.toLowerCase();

//             switch (dataType) {
//                 case 'string': case 'number': case 'boolean':
//                     schemaDef.type = dataType;
//                     break;
//                 case 'object':
//                     schemaDef.type = 'object';
//                     const nested = convert(field.children || []);
//                     schemaDef.properties = nested.properties;
//                     if (nested.required.length > 0) schemaDef.required = nested.required;
//                     break;
//                 case 'array':
//                     schemaDef.type = 'array';
//                     const itemType = field.arrayItemType?.toLowerCase() || 'string';
//                     if (itemType === 'object') {
//                         const nestedArray = convert(field.children || []);
//                         schemaDef.items = {
//                             type: 'object',
//                             properties: nestedArray.properties,
//                         };
//                         if (nestedArray.required.length > 0) {
//                             schemaDef.items.required = nestedArray.required;
//                         }
//                     } else {
//                         schemaDef.items = { type: itemType };
//                     }
//                     break;
//                 default:
//                     schemaDef.type = 'string';
//             }
//             properties[field.fieldName] = schemaDef;
//             if (field.required) required.push(field.fieldName);
//         });
//         return { properties, required };
//     };

//     const root = convert(fields);
//     return {
//         title: modelName,
//         type: 'object',
//         properties: root.properties,
//         ...(root.required.length > 0 && { required: root.required }),
//     };
// };

const ActionNodeModal = ({ mode, nodeData, onClose, onSave, onDelete, appData, viewOnly }) => {
  const [app, setApp] = useState(nodeData?.data?.app || "");
  const [functionsList, setFunctionsList] = useState(nodeData?.data?.functions || []);
  const [actionInstructions, setActionInstructions] = useState(nodeData?.data?.actionInstructions || "");
  const [outputExpectation, setOutputExpectation] = useState(nodeData?.data?.outputExpectation || "");
  const [addingFunction, setAddingFunction] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const { userGoogleId } = DataState();

  // Add these new states
  const [actionNeedsApproval, setActionNeedsApproval] = useState(nodeData?.data?.actionNeedsApproval || false);
  const [functionsForApproval, setFunctionsForApproval] = useState(nodeData?.data?.functionsForApproval || []);
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  // --- NEW STATE FOR JSON OUTPUT ---
  const [jsonOutput, setJsonOutput] = useState(nodeData?.data?.jsonOutput || false);
  const [jsonOutputSchema, setJsonOutputSchema] = useState(nodeData?.data?.jsonOutputSchema || { title: 'Output', type: 'object', properties: {} });
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);

  // Store the initial action instructions for comparison on save.
  const initialActionInstructions = useRef(nodeData?.data?.actionInstructions || "");

  // Find the currently selected app and its functions list.
  const selectedApp = appData.find((a) => a.value === app);
  const validFunctions = selectedApp?.functions || [];

  // Compute remaining functions that can be added manually.
  const remainingFunctions = validFunctions.filter(
    (f) => !functionsList.includes(f.label)
  );

   // Memoize the initial fields for the schema builder
  const initialSchemaFields = useMemo(() => schemaToFields(jsonOutputSchema), [jsonOutputSchema]);

  // --- NEW HANDLER for saving from SchemaBuilderModal ---
  const handleSchemaSave = ({ fields, modelName }) => {
    const newSchema = fieldsToSchema(fields, modelName);
    setJsonOutputSchema(newSchema);
    setIsBuilderOpen(false); // Close the builder modal
    toast.success("Output schema saved!");
  };


  // Helper to call the AI endpoint.
  const callDetermineFunctionsAPI = async () => {
    const inputNodes = (nodeData?.inputNodes || []).map((node) => {

      return {
        type: node.type, data: node.data
      }
    })
    console.log("Input Nodes for AI Determination:", inputNodes)
    setLoadingAI(true);
    try {
      const response = await fetch(`${API_URL}/canvas/actionNode/determineFunctions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${userGoogleId}` // Dummy token added here
        },
        body: JSON.stringify({
          allFunctions: validFunctions.map((f) => f.label),
          previousFunctions: functionsList,
          // functions: validFunctions,
          inputNodes,
          app,
          actionInstructions,
        }),
        credentials: "include"
      });
      if (!response.ok) {
        throw new Error("API request failed");
      }
      const data = await response.json();
      console.log("AI Determined Action Functions:", data)
      const newFunctions = data.functions || [];
      setFunctionsList(newFunctions);

      // Filter out any approved functions that are no longer in the main functions list
      setFunctionsForApproval(prev => prev.filter(f => newFunctions.includes(f)));

      initialActionInstructions.current = actionInstructions;
      toast.success("Functions updated successfully");
      return newFunctions;
    } catch (error) {
      console.error("Error calling determine functions API:", error);
      toast.error("Failed to determine functions. Please try again.");
      return null;
    } finally {
      setLoadingAI(false);
    }
  };

  // Final save function that actually saves the node.
  // Accept an optional functionsOverride parameter.
  const performSave = (functionsOverride) => {
    // If approval is disabled, ensure the approval list is empty for clean data.
    const finalFunctionsForApproval = actionNeedsApproval ? functionsForApproval : [];

    const updatedNode = {
      ...nodeData,
      data: {
        app,
        label: selectedApp?.label,
        functions: functionsOverride !== undefined ? functionsOverride : functionsList,
        actionInstructions,
        outputExpectation,
        actionNeedsApproval, // Add this line
        functionsForApproval: finalFunctionsForApproval, // And this line
        // --- ADD NEW FIELDS TO SAVED DATA ---
        jsonOutput,
        jsonOutputSchema: jsonOutput ? jsonOutputSchema : {}, // Save schema only if enabled
      },
    };
    onSave(updatedNode);
  };

  // Handle save button click.
  const handleSave = async () => {
    // Check if action instructions have changed (after trimming)
    if (
      actionInstructions.trim() &&
      actionInstructions.trim() !== initialActionInstructions.current.trim()
    ) {
      // Show warning modal to choose between using AI or ignoring.
      setShowWarningModal(true);
    } else {
      performSave();
    }
  };

  // Handle removal of a function from the functions list.
  const handleRemoveFunction = (index) => {
    const functionToRemove = functionsList[index];
    const updatedFunctions = [...functionsList];
    updatedFunctions.splice(index, 1);
    setFunctionsList(updatedFunctions);
    // Also remove the function from the approval list if it exists there
    setFunctionsForApproval(prev => prev.filter(f => f !== functionToRemove));
  };

  // Render warning modal.
  const renderWarningModal = () => (
    <Modal
      title="Warning"
      isOpen={true}
      onClose={() => setShowWarningModal(false)}
    >
      <div className="p-4 bg-zinc-900 rounded-lg">
        <p className="text-zinc-100 mb-4">
          The action instructions have changed. Would you like to use AI to determine the functions required for the action execution?
        </p>
        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md transition duration-200 focus:outline-none focus:ring-2 focus:ring-zinc-500"
            onClick={() => {
              // Ignore: Just close warning modal.
              setShowWarningModal(false);
              performSave();
            }}
          >
            Ignore
          </button>
          <button
            className="px-4 py-2 text-zinc-300 bg-[#5652CF] hover:bg-blue-600 border border-blue-600 rounded-md transition duration-200 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
            onClick={async () => {
              // Use AI: Call API, update functions list, and update the stored instructions.
              const newFuncs = await callDetermineFunctionsAPI();
              if (newFuncs) {
                // Update the stored "initial" instructions so that further saves won't trigger the warning.
                // initialActionInstructions.current = actionInstructions;
                toast.success("Functions updated based on action instructions");
              }
              // Close warning modal but keep the modal open for further review.
              setShowWarningModal(false);
            }}
            disabled={loadingAI}
          >
            {loadingAI ? "Loading..." : "Determine using AI"}
          </button>
        </div>
      </div>
    </Modal>
  );

  // Render modal for managing functions that need approval.
  const renderApprovalModal = () => (
    <Modal
      title="Manage Functions for Approval"
      isOpen={showApprovalModal}
      onClose={() => setShowApprovalModal(false)}
    >
      <div className="p-4 bg-zinc-900 rounded-lg">
        <p className="text-zinc-300 mb-4">Select the functions that require approval before execution.</p>
        <div className="space-y-2 max-h-60 overflow-y-auto mb-4 border border-zinc-700 p-3 rounded-md">
          {functionsList.length > 0 ? (
            functionsList.map((func) => (
              <div key={func} className="flex items-center">
                <input
                  type="checkbox"
                  id={`func-approval-${func}`}
                  checked={functionsForApproval.includes(func)}
                  onChange={() => {
                    setFunctionsForApproval(prev =>
                      prev.includes(func)
                        ? prev.filter(f => f !== func)
                        : [...prev, func]
                    );
                  }}
                  className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-[#5652CF] focus:ring-blue-600"
                />
                <label htmlFor={`func-approval-${func}`} className="ml-3 block text-sm font-medium text-zinc-100">
                  {func}
                </label>
              </div>
            ))
          ) : (
            <p className="text-zinc-400 text-center">No functions have been added to this action yet.</p>
          )}
        </div>
        <div className="flex justify-end">
          <button
            className="px-4 py-2 text-white bg-[#5652CF] hover:bg-blue-600 rounded-md transition duration-200"
            onClick={() => setShowApprovalModal(false)}
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );

  return (
    <>
      <Modal
        title={viewOnly ? "Action Node" : mode === "add" ? "Add Action Node" : "Edit Action Node"}
        isOpen={true}
        onClose={onClose}
      >
        <div className="p-4 h-[80vh] overflow-y-auto bg-zinc-900 rounded-lg shadow-md">
          <div className="p-4">
            <label className="block text-sm font-medium text-zinc-200 mb-2">
              {viewOnly ? "App" : "Select App"}
            </label>
            <select
              disabled={viewOnly}
              className="w-full border border-zinc-700 bg-zinc-800 text-zinc-100 p-2 rounded focus:outline-none focus:ring-2 focus:ring-[#5652CF] mb-4"
              value={app}
              onChange={(e) => {
                setApp(e.target.value);
                // Reset functions when app changes.
                setFunctionsList([]);
                setFunctionsForApproval([]); // Also reset the approval list
              }}
            >
              <option value="">Select App</option>
              {appData.map((app) => (
                <option key={app.value} value={app.value}>
                  {app.label}
                </option>
              ))}
            </select>

            {app && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-zinc-200">Functions</label>
                  {!viewOnly && (
                    <button
                      className="bg-[#5652CF] text-white p-2 rounded"
                      onClick={callDetermineFunctionsAPI}
                      disabled={loadingAI || !actionInstructions.trim()}
                    >
                      {loadingAI ? "Loading..." : "AI"}
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {functionsList.map((func, idx) => (
                    <div key={idx} className="flex items-center bg-zinc-800 px-3 py-1 rounded">
                      <span className="text-zinc-100">{func}</span>
                      {!viewOnly && (
                        <button
                          className="ml-2 text-red-500"
                          onClick={() => handleRemoveFunction(idx)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  {!viewOnly && (
                    <div className="flex items-center">
                      {addingFunction ? (
                        <select
                          className="bg-zinc-800 text-zinc-100 p-2 rounded border border-zinc-700"
                          value=""
                          onChange={(e) => {
                            const newFunc = e.target.value;
                            if (newFunc) {
                              setFunctionsList([...functionsList, newFunc]);
                            }
                            setAddingFunction(false);
                          }}
                          onBlur={() => setAddingFunction(false)}
                          autoFocus
                        >
                          <option value="">Select function</option>
                          {remainingFunctions.map((f) => (
                            <option key={f.label} value={f.label}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <button
                          className="bg-[#5652CF] text-white p-2 rounded"
                          onClick={() => setAddingFunction(true)}
                        >
                          +
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            <label className="block text-sm font-medium text-zinc-200 mt-4">
              Action Instructions
            </label>

            <AutoGrowTextarea
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-md placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF] focus:border-transparent transition duration-200 resize-none"
              placeholder="Enter instructions for this action"
              value={actionInstructions}
              disabled={viewOnly}
              onChange={(e) => setActionInstructions(e.target.value)}

            />

            <label className="block text-sm font-medium text-zinc-200 mt-4">
              Output Expectation
            </label>
            <AutoGrowTextarea
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-zinc-100 rounded-md placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#5652CF] focus:border-transparent transition duration-200 resize-none"
              placeholder="Enter output expectation"
              value={outputExpectation}
              disabled={viewOnly}
              onChange={(e) => setOutputExpectation(e.target.value)}
            />

            {/* --- NEW JSON OUTPUT SECTION --- */}
            {!viewOnly && (
                <div className="mt-4 pt-4 border-t border-zinc-700">
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="jsonOutput"
                            checked={jsonOutput}
                            onChange={(e) => setJsonOutput(e.target.checked)}
                            className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-[#5652CF] focus:ring-blue-600"
                        />
                        <label htmlFor="jsonOutput" className="text-sm font-medium text-zinc-200">
                            Enable Structured JSON Output
                        </label>
                    </div>

                    {jsonOutput && (
                        <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg">
                            <p className="text-sm text-zinc-400 mb-2">
                                {Object.keys(jsonOutputSchema?.properties || {}).length > 0
                                ? `Schema defined with ${Object.keys(jsonOutputSchema.properties).length} top-level fields.`
                                : "No output schema defined yet."
                                }
                            </p>
                            <button
                                onClick={() => setIsBuilderOpen(true)}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-zinc-100 bg-blue-600 hover:bg-blue-700 rounded-md transition duration-200"
                            >
                                <Edit size={14} />
                                {Object.keys(jsonOutputSchema?.properties || {}).length > 0 ? "Edit Schema" : "Create Schema"}
                            </button>
                        </div>
                    )}
                </div>
            )}


            {/* --- Approval Section --- */}
            {!viewOnly && (
              <>
                <div className="flex items-center gap-3 mt-4">
                  <input
                    type="checkbox"
                    id="actionNeedsApproval"
                    checked={actionNeedsApproval}
                    onChange={(e) => {
                      setActionNeedsApproval(e.target.checked);
                      // If turning off approval, clear the list of functions
                      if (!e.target.checked) {
                        setFunctionsForApproval([]);
                      }
                    }}
                    className="h-4 w-4 rounded border-zinc-600 bg-zinc-800 text-[#5652CF] focus:ring-blue-600"
                  />
                  <label htmlFor="actionNeedsApproval" className="text-sm font-medium text-zinc-200">
                    This action requires approval
                  </label>
                </div>

                {actionNeedsApproval && (
                  <div className="mt-2">
                    <button
                      onClick={() => setShowApprovalModal(true)}
                      className="px-3 py-1 text-sm text-zinc-300 bg-zinc-700 hover:bg-zinc-600 border border-zinc-600 rounded-md transition duration-200"
                    >
                      Manage Functions for Approval ({functionsForApproval.length} selected)
                    </button>
                  </div>
                )}
              </>
            )}

            {!viewOnly && (
              <div className="flex justify-end gap-3 mt-4">
                {mode === "edit" && (
                  <button
                    className="px-4 py-2 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md transition duration-200 focus:outline-none focus:ring-2 focus:ring-zinc-500"
                    onClick={() => onDelete(nodeData.id)}
                  >
                    Delete Node
                  </button>
                )}
                <button
                  className="px-4 py-2 text-zinc-300 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded-md transition duration-200 focus:outline-none focus:ring-2 focus:ring-zinc-500"
                  onClick={handleSave}
                >
                  {mode === "add" ? "Add Node" : "Save Changes"}
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>
      {/* --- RENDER SCHEMA BUILDER MODAL --- */}
      <SchemaBuilderModal
        isOpen={isBuilderOpen}
        onClose={() => setIsBuilderOpen(false)}
        onSave={handleSchemaSave}
        initialFields={initialSchemaFields}
        initialModelName={jsonOutputSchema?.title || 'ActionOutput'}
      />
      {showWarningModal && renderWarningModal()}
      {showApprovalModal && renderApprovalModal()} {/* Add this line */}
    </>
  );
};

export default ActionNodeModal;
